<template>
  <BasicTemplate>
    <v-card class="feed-ordering">
      <v-card-title class="d-flex align-center">
        <h3 class="d-flex align-center mb-0">
          <v-icon class="mr-2">mdi-sort</v-icon>
          Feed Game Ordering
        </h3>
      </v-card-title>

      <v-dialog v-model="saving" width="480" persistent>
        <v-card class="admin-dialog">
          <v-card-title class="admin-dialog__title">
            Saving feed order...
          </v-card-title>
          <v-card-text class="admin-dialog__body">
            <v-progress-linear
              :value="savingProgress"
              :indeterminate="!savingTotal"
              color="primary"
              background-color="grey lighten-3"
              height="8"
              rounded
              class="mb-3"
            />
            <div class="text-center">
              {{ savingProgressText }}
            </div>
          </v-card-text>
        </v-card>
      </v-dialog>

      <v-card-text>
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <v-alert v-if="error" type="error" class="mb-4">
          {{ error }}
        </v-alert>

        <v-card class="mb-4 feed-ordering__section" elevation="1">
          <v-card-title class="d-flex align-center feed-ordering__section-title">
            <v-icon class="mr-2">mdi-rss</v-icon>
            <span class="font-weight-bold">Select Feed</span>
          </v-card-title>
          <v-card-text>
            <v-select
              v-model="selectedFeed"
              :items="feeds"
              item-text="name"
              item-value="id"
              label="Choose a feed to manage"
              data-testid="feed-ordering-select"
              dense
              hide-details
              clearable
              @change="loadFeedGames"
            />
          </v-card-text>
        </v-card>

        <template v-if="selectedFeed">
          <v-card class="mb-4 feed-ordering__games" elevation="1">
            <v-card-title class="d-flex align-center">
              <v-icon class="mr-2">mdi-gamepad-variant</v-icon>
              <span class="font-weight-bold">Games</span>
              <v-spacer />
              <v-chip small class="mr-2" color="info">
                {{ games.length }} games
              </v-chip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    class="mr-2"
                    color="secondary"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="!games.length"
                    data-testid="feed-ordering-auto-order"
                    @click="autoOrderGames"
                  >
                    mdi-auto-fix
                  </v-icon>
                </template>
                <span>Auto-order all games sequentially</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    color="secondary"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="!games.length"
                    data-testid="feed-ordering-clear-orders"
                    @click="clearAllFeedOrders"
                  >
                    mdi-broom
                  </v-icon>
                </template>
                <span>Clear all feed order values</span>
              </v-tooltip>
            </v-card-title>

            <v-card-text class="feed-ordering__search py-1">
              <v-text-field
                v-model="search"
                label="Search"
                dense
                hide-details
                clearable
                class="mt-0"
                prepend-icon="mdi-magnify"
              />
            </v-card-text>

            <v-data-table
              class="feed-ordering__table"
              :headers="headers"
              :items="filteredGames"
              :items-per-page="-1"
              hide-default-footer
              :loading="loading"
            >
              <template v-slot:body="{ items }">
                <draggable
                  v-model="games"
                  group="games"
                  handle=".drag-handle"
                  tag="tbody"
                  @start="drag = true"
                  @end="updateOrderValues"
                >
                  <tr
                    v-for="game in items"
                    :key="game.id"
                    class="game-item"
                    :class="{ dragging: drag }"
                  >
                    <td>
                      <div class="d-flex align-center">
                        <v-icon class="drag-handle mr-2" color="grey">mdi-drag</v-icon>
                        <div>
                          <div class="game-name">{{ game.name }}</div>
                          <div class="game-slug grey--text">{{ game.slug }}</div>
                        </div>
                      </div>
                    </td>
                    <td>{{ game.gamesOrder }}</td>
                    <td>{{ game.feedOrder }}</td>
                    <td>
                      <v-text-field
                        v-model.number="game.meta.order"
                        type="number"
                        min="1"
                        dense
                        hide-details
                        single-line
                        class="order-input"
                        clearable
                        @change="handleManualOrderChange(game)"
                      />
                    </td>
                    <td>
                      <div class="feed-ordering__row-actions">
                        <v-icon
                          color="primary"
                          :disabled="games.length <= 1 || !game.meta.order || game.meta.order <= 1"
                          @click="decrementOrder(game)"
                        >
                          mdi-arrow-up-bold
                        </v-icon>
                        <v-icon
                          color="primary"
                          :disabled="games.length <= 1 || !game.meta.order || game.meta.order >= games.length"
                          @click="incrementOrder(game)"
                        >
                          mdi-arrow-down-bold
                        </v-icon>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </template>
              <template v-slot:no-data>
                <div class="text-center pa-6">
                  <v-icon size="64" color="grey lighten-1">mdi-sort-variant</v-icon>
                  <p class="mt-2 mb-0 grey--text">No games in this feed.</p>
                </div>
              </template>
            </v-data-table>
          </v-card>

          <div class="feed-ordering__save mb-2">
            <v-btn
              color="primary"
              large
              class="mr-2"
              data-testid="feed-ordering-save"
              :loading="saving"
              :disabled="!hasChanges"
              @click="saveOrdering"
            >
              Save
            </v-btn>
            <v-btn
              outlined
              large
              data-testid="feed-ordering-cancel"
              :disabled="saving || !hasChanges"
              @click="cancelChanges"
            >
              Cancel
            </v-btn>
          </div>
        </template>
      </v-card-text>
    </v-card>
  </BasicTemplate>
</template>

<script>
import BasicTemplate from '../../templates/basic.vue';
import draggable from 'vuedraggable';

export default {
  name: 'FeedOrdering',
  components: {
    BasicTemplate,
    draggable
  },
  data() {
    return {
      search: '',
      feeds: [],
      selectedFeed: null,
      games: [],
      drag: false,
      loading: true,
      saving: false,
      savingTotal: 0,
      savingCompleted: 0,
      savingStatusText: '',
      error: null,
      headers: [
        { text: 'Game', value: 'name', sortable: false },
        { text: 'Global Order', value: 'gamesOrder', sortable: false, width: '110px' },
        { text: 'Feed Order', value: 'feedOrder', sortable: false, width: '110px' },
        { text: 'Actual Order', value: 'order', sortable: false, width: '130px' },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          align: 'center',
          width: '1%',
          class: 'feed-ordering__col--compact',
          cellClass: 'feed-ordering__col--compact',
        },
      ]
    };
  },
  computed: {
    filteredGames() {
      let filtered = this.games;
      if (this.search) {
        const search = this.search.toLowerCase();
        filtered = filtered.filter(game => 
          game.name.toLowerCase().includes(search) || 
          game.slug.toLowerCase().includes(search)
        );
      }
      // Sort by meta.order (current order)
      return filtered.sort((a, b) => (a.meta?.order || Number.MAX_SAFE_INTEGER) - (b.meta?.order || Number.MAX_SAFE_INTEGER));
    },
    hasChanges() {
      // Compare current meta.order with the value it had *after* initial loading logic
      return this.games.some(game => game.meta.order !== game.initialMetaOrder);
    },
    savingProgress() {
      if (!this.savingTotal) {
        return 0;
      }
      return Math.round((this.savingCompleted / this.savingTotal) * 100);
    },
    savingProgressText() {
      if (!this.savingTotal) {
        return this.savingStatusText || 'Preparing...';
      }
      return this.savingStatusText
        || `Saved ${this.savingCompleted} / ${this.savingTotal} (${this.savingProgress}%)`;
    },
  },
  async created() {
    try {
      this.loading = true;
      await this.loadFeeds();
    } catch (error) {
      console.error('Error in created:', error);
      this.error = error.message || 'Failed to load feeds';
      this.$swal.fire({
        icon: 'error',
        title: 'Error Loading Feeds',
        text: error.message || 'Failed to load feeds',
        footer: 'Please check the console for more details'
      });
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async loadFeeds() {
      try {
        this.loading = true;
        const response = await this.$gameApi.get('/feeds');
        if (!Array.isArray(response)) {
          throw new Error('Invalid response format from feeds API');
        }
        this.feeds = response;
      } catch (error) {
        console.error('Error loading feeds:', error);
        this.error = error.message || 'Failed to load feeds';
        throw error;
      } finally {
        this.loading = false;
      }
    },
    cancelChanges() {
      if (!this.hasChanges || this.saving) return;
      this.games = this.games
        .map((game) => ({
          ...game,
          meta: {
            ...game.meta,
            order: game.initialMetaOrder,
          },
        }))
        .sort(
          (a, b) =>
            (a.meta?.order ?? Number.MAX_SAFE_INTEGER) - (b.meta?.order ?? Number.MAX_SAFE_INTEGER),
        );
    },
    async loadFeedGames() {
      if (!this.selectedFeed) {
        this.games = [];
        this.search = '';
        return;
      }

      try {
        this.loading = true;
        this.error = null;

        // short=true: feedGames + game (+ order); skip full meta/thumbs tree
        const feedResponse = await this.$gameApi.get(`/feeds/${this.selectedFeed}?short=true`);
        
        // ** Access the feedGames collection from the response **
        const feedGamesCollection = feedResponse?.feedGames;

        // Check if feedGamesCollection is an array
        if (!Array.isArray(feedGamesCollection)) {
          // Handle cases where feedGames collection is missing or not an array
          console.warn(`No 'feedGames' array found in response for feed ${this.selectedFeed}, or it's not an array.`);
          this.games = []; // Set games to empty
          throw new Error(`Invalid response format: Missing or invalid 'feedGames' array for feed ${this.selectedFeed}.`);
        }

        // 2. Process the feedGames collection
        this.games = feedGamesCollection
          .filter(item => item && item.game && item.game.id) // Ensure valid items with nested game and game.id
          .map(item => {
            const game = item.game; // Access the nested game object

            // Extract orders based on user clarification:
            // Feed Order is directly in the item
            const specificFeedOrder = item.order ?? null;
            // Global Order is within the nested game object
            const globalOrder = game.order ?? null;

            // Determine initial Actual Order logic
            const initialActualOrder = specificFeedOrder !== null ? specificFeedOrder : (globalOrder !== null ? globalOrder : null);

            // Construct the game object for the table
            return {
              ...game, // Spread properties from the nested game object (id, name, slug, etc.)
              feedOrder: specificFeedOrder, // For display in 'Feed Order' column
              gamesOrder: globalOrder, // For display in 'Global Order' column
              meta: {
                ...game.meta, // Keep existing meta properties
                order: initialActualOrder // Initial value for 'Actual Order' input
              },
              initialMetaOrder: initialActualOrder // Store initial state for change detection
            };
          })
          // 3. Sort the final list by the initial actual order
          .sort((a, b) => (a.meta?.order || Number.MAX_SAFE_INTEGER) - (b.meta?.order || Number.MAX_SAFE_INTEGER));

        console.log('Processed games from single feed endpoint:', this.games); // Debug log

      } catch (error) {
        console.error('Error loading feed games:', error);
        this.error = error.message || 'Failed to load feed games';
        this.$swal.fire({
          icon: 'error',
          title: 'Error Loading Feed Games',
          text: error.message || `Could not load game details for feed ${this.selectedFeed}.`,
          footer: 'Please check the console for more details'
        });
        this.games = []; // Clear games on error
      } finally {
        this.loading = false;
      }
    },
    updateOrderValues() {
      this.drag = false;
      // Update order values for all games in the list
      this.games = this.games.map((game, index) => ({
        ...game,
        meta: {
          ...game.meta,
          order: index + 1
        }
      }));
    },
    handleManualOrderChange(game) {
      const rawValue = game.meta.order;

      if (rawValue === '' || rawValue === null) {
        game.meta.order = null;
        // Check if removing the order necessitates recalculation (optional, depends on desired behavior)
        // For now, just setting null as requested.
        return;
      }

      let newOrder = parseInt(rawValue);
      if (isNaN(newOrder) || newOrder < 1) newOrder = 1;
      // Clamp max value? Only relevant if recalculating.
      // Let's clamp it for consistency during recalculation phase.
      if (newOrder > this.games.length) newOrder = this.games.length;

      // Check if another game already has this order
      const duplicateExists = this.games.some(
        g => g.id !== game.id && g.meta.order === newOrder
      );

      if (duplicateExists) {
        // Recalculate if duplicate is entered
        game.meta.order = newOrder; // Apply the desired order first

        const oldIndex = this.games.findIndex(g => g.id === game.id);
        if (oldIndex !== -1) {
          this.games.splice(oldIndex, 1); // Remove from old position
          this.games.splice(newOrder - 1, 0, game); // Insert at new position
          
          // Reassign meta.order sequentially for all games
          this.games.forEach((g, i) => {
            // Use Vue.set for reactivity if direct assignment causes issues, but often not needed for arrays
            g.meta.order = i + 1;
          });
        }
      } else {
        // If no duplicate, just update the value for the current game
        game.meta.order = newOrder;
      }
    },
    incrementOrder(game) {
      // Swap meta.order with the item below
      const sortedGames = [...this.games].sort((a, b) => (a.meta?.order || Number.MAX_SAFE_INTEGER) - (b.meta?.order || Number.MAX_SAFE_INTEGER));
      const currentIndex = sortedGames.findIndex(g => g.id === game.id);

      if (currentIndex < sortedGames.length - 1 && currentIndex !== -1) {
        const gameBelow = sortedGames[currentIndex + 1];
        if (game.meta.order !== null && gameBelow.meta.order !== null) {
            const currentOrder = game.meta.order;
            const orderBelow = gameBelow.meta.order;
            this.$set(game.meta, 'order', orderBelow);
            this.$set(gameBelow.meta, 'order', currentOrder);
            this.games = [...this.games]; // Force re-sort
        } else {
           console.warn('Cannot swap order with an item that has a null order.');
        }
      }
    },
    decrementOrder(game) {
      // Swap meta.order with the item above
      const sortedGames = [...this.games].sort((a, b) => (a.meta?.order || Number.MAX_SAFE_INTEGER) - (b.meta?.order || Number.MAX_SAFE_INTEGER));
      const currentIndex = sortedGames.findIndex(g => g.id === game.id);

      if (currentIndex > 0 && currentIndex !== -1) {
        const gameAbove = sortedGames[currentIndex - 1];
        if (game.meta.order !== null && gameAbove.meta.order !== null) {
            const currentOrder = game.meta.order;
            const orderAbove = gameAbove.meta.order;
            this.$set(game.meta, 'order', orderAbove);
            this.$set(gameAbove.meta, 'order', currentOrder);
            this.games = [...this.games]; // Force re-sort
        } else {
           console.warn('Cannot swap order with an item that has a null order.');
        }
      }
    },
    async saveOrdering() {
      if (!this.selectedFeed) {
        this.$swal.fire({
          icon: 'warning',
          title: 'No Feed Selected',
          text: 'Please select a feed to manage its ordering',
        });
        return;
      }

      const changedGames = this.games.filter(
        (game) => game.meta.order !== game.initialMetaOrder,
      );

      if (changedGames.length === 0) {
        this.$swal.fire({
          icon: 'info',
          title: 'No Changes',
          text: 'No changes were made to save',
        });
        return;
      }

      const result = await this.$swal.fire({
        title: 'Save Feed Ordering?',
        text: `This will update the feed order for ${changedGames.length} game(s).`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Yes, save it!',
        cancelButtonText: 'No, cancel',
      });
      if (!result.isConfirmed) {
        return;
      }

      try {
        this.saving = true;
        this.error = null;
        this.savingTotal = changedGames.length;
        this.savingCompleted = 0;
        this.savingStatusText = 'Preparing batches...';
        await this.$nextTick();

        const BATCH_CHUNK_SIZE = 50;
        const operations = changedGames.map((game) => ({
          operation: 'update',
          data: {
            gameId: game.id,
            order: game.meta.order ?? null,
          },
        }));
        const totalBatches = Math.ceil(operations.length / BATCH_CHUNK_SIZE);

        for (let i = 0; i < operations.length; i += BATCH_CHUNK_SIZE) {
          const chunk = operations.slice(i, i + BATCH_CHUNK_SIZE);
          const batchIndex = Math.floor(i / BATCH_CHUNK_SIZE) + 1;
          this.savingStatusText = `Saving batch ${batchIndex} / ${totalBatches} (${this.savingCompleted} / ${this.savingTotal})`;
          await this.$nextTick();

          const batchResponse = await this.$gameApi.post(
            `/feeds/${this.selectedFeed}/games/batch`,
            { operations: chunk },
          );
          if (!batchResponse || !batchResponse.success) {
            const firstError = batchResponse?.results?.find((r) => r.error);
            throw new Error(firstError?.error || 'Batch save failed');
          }
          this.savingCompleted = Math.min(i + chunk.length, changedGames.length);
          this.savingStatusText = `Saved ${this.savingCompleted} / ${this.savingTotal} (${this.savingProgress}%)`;
          await this.$nextTick();
        }

        this.savingStatusText = 'Reloading feed games...';
        await this.loadFeedGames();

        this.$swal.fire({
          icon: 'success',
          title: 'Success',
          text: `Game ordering has been saved for ${changedGames.length} game${changedGames.length === 1 ? '' : 's'}`,
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error('Error saving ordering:', error);
        this.error = error.message || 'Failed to save game ordering';
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to save game ordering',
        });
      } finally {
        this.saving = false;
        this.savingTotal = 0;
        this.savingCompleted = 0;
        this.savingStatusText = '';
      }
    },
    autoOrderGames() {
      this.games.forEach((game, index) => {
        game.meta.order = index + 1;
      });
      // Ensure table reflects changes if sorting depends on meta.order
      this.games = [...this.games];
    },
    clearAllFeedOrders() {
      this.games = this.games.map(game => ({
        ...game,
        feedOrder: null,
        meta: {
          ...game.meta,
          order: null
        }
      }));
    }
  }
};
</script>

<style scoped>
.feed-ordering__section {
  border-left: 4px solid #1976d2;
  overflow: hidden;
}

.feed-ordering__section-title {
  display: flex;
  align-items: center;
  padding-top: 16px !important;
  padding-bottom: 12px !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em;
}

.feed-ordering__section ::v-deep > .v-card-text {
  padding-top: 4px !important;
  padding-bottom: 16px !important;
}

.feed-ordering__games {
  overflow: hidden;
}

.feed-ordering__search {
  max-width: 420px;
}

.feed-ordering__table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.feed-ordering__table ::v-deep .feed-ordering__col--compact {
  white-space: nowrap;
  width: 1%;
  padding-left: 8px;
  padding-right: 8px;
}

.feed-ordering__row-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
}

.feed-ordering__save {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  position: sticky;
  bottom: 12px;
  z-index: 2;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(25, 118, 210, 0.2);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(6px);
}

.drag-handle {
  cursor: move;
}

.game-item {
  transition: background-color 0.2s ease;
}

.game-item.dragging {
  background-color: rgba(0, 0, 0, 0.05);
}

.game-name {
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.2;
}

.game-slug {
  font-size: 0.75rem;
  line-height: 1.2;
}

.order-input {
  max-width: 80px;
}

.order-input ::v-deep input[type='number']::-webkit-outer-spin-button,
.order-input ::v-deep input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.order-input ::v-deep input[type='number'] {
  -moz-appearance: textfield;
}
</style>